// ============================================================================
//  dAdmin  ›  dAttendance  ›  Approvals        (spec page 3)
//
//  Three request types land here:
//    Edit request     → Accept / Reject   (reopens a locked sheet)
//    Approval request → Approve / Reject  (closes the month)
//    Weekend support  → Approve / Reject  (an off-day the employee worked;
//                       approving flips that ONE day to a working P)
//
//  The buttons differ by type on purpose. "Accept" and "Approve" mean different
//  things to the person on the other end, and the server rejects the wrong verb
//  rather than quietly mapping it.
//
//  View sheet opens the actual month before deciding — approving a number you
//  haven't looked at is how a wrong payslip gets signed off.
// ============================================================================
import React, { useState, useEffect, useCallback } from "react";
import { FaFileExcel } from "react-icons/fa";
import { apiFetch } from "./utils/api";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./dAttendance_Approvals.css";

const MONTHS = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];
const FILTERS = ["pending", "accepted", "approved", "rejected", "all"];

const TYPE_META = {
    edit:     { label: "Edit request",    cls: "is-orange" },
    approval: { label: "Approval request", cls: "is-navy" },
    support:  { label: "Weekend support",  cls: "is-amber" },
};
const typeMeta = (t) => TYPE_META[t] || TYPE_META.approval;

// "2026-09-08" -> "8 Sep 2026"
const fmtDay = (ymd) => {
    if (!ymd) return "";
    const [y, m, d] = ymd.split("-");
    return `${Number(d)} ${MONTHS[Number(m) - 1].slice(0, 3)} ${y}`;
};
const portionLabel = (p) => (p === "half" ? "half day" : "full day");

export default function DAttendanceApprovals({ navSize }) {
    const [filter, setFilter] = useState("pending");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [detail, setDetail] = useState(null);
    const [rejectReq, setRejectReq] = useState(null);   // row awaiting a rejection reason
    const [rejectNote, setRejectNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [toast, setToast] = useState(null);

    const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 2600); };

    // Download the employee's month sheet as .xlsx — same file the employee gets.
    const exportSheet = async (empId, y, m) => {
        try {
            const res = await apiFetch(
                `/api/dattendance/approvals/export?emp_id=${encodeURIComponent(empId)}&year=${y}&month=${m}`);
            if (!res.ok) {
                const j = await res.json().catch(() => ({}));
                throw new Error(j.error || "Export failed");
            }
            // Use the server's filename (workbookFilename) so it matches the
            // employee download exactly, e.g. DZIND148_Attendance_Sheet_2026_08.xlsx.
            const cd = res.headers.get("Content-Disposition") || "";
            const m2 = /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(cd);
            const fname = (m2 && decodeURIComponent(m2[1]).trim())
                || `${empId}_Attendance_Sheet_${y}_${String(m).padStart(2, "0")}.xlsx`;
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fname;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            flash(e.message || "Export failed");
        }
    };

    const load = useCallback(async (status) => {
        setLoading(true); setError("");
        try {
            const res = await apiFetch(`/api/dattendance/approvals/list?status=${status}`);
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || "Failed to load");
            setRows(json.requests);
        } catch (err) { setRows([]); setError(err.message); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(filter); }, [filter, load]);

    const openSheet = async (request_id) => {
        try {
            const res = await apiFetch(`/api/dattendance/approvals/sheet/${request_id}`);
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || "Could not open the sheet");
            setDetail(json);
        } catch (err) { flash(err.message); }
    };

    const decide = async (request_id, decision, note) => {
        setBusy(true);
        try {
            const res = await apiFetch("/api/dattendance/approvals/decide", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ request_id, decision, note: note || null }),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.error || "Failed");
            flash(`Marked ${json.request_status}`);
            setDetail(null);
            await load(filter);
        } catch (err) { flash(err.message); }
        finally { setBusy(false); }
    };

    return (
        <div className="dspr-container">
          <div className={`dspr-dashboard ${navSize || ""}`}>
            <div className="dspr-header-wrapper">
              <h2>
                <img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} />
                Approvals
              </h2>
              <p className="dspr-subtitle">
                Everything coming in from the dAttendance app. Edit requests reopen a submitted
                sheet; approval requests close the month; weekend-support claims flip a single
                worked off-day to present. Managers see only what is routed to them; admins see
                the whole queue.
              </p>
            </div>
            <div className="dspr-dashboard-content datt-page">
              <div className="datt-toolbar">
                <div className="datt-filters">
                    {FILTERS.map((f) => (
                        <button key={f} type="button"
                            className={`datt-btn is-sm ${filter === f ? "is-dark" : "is-ghost"}`}
                            onClick={() => setFilter(f)}>
                            {f[0].toUpperCase() + f.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {error && <div className="datt-notice is-red">{error}</div>}

            <div className="datt-card">
                {loading ? (
                    <div className="datt-loading">Loading…</div>
                ) : (
                    <div className="datt-table-wrap">
                        <table className="datt-table datt-req-table">
                            <thead>
                                <tr>
                                    <th>Raised</th><th>Employee</th><th>Period</th>
                                    <th>Request type</th><th>Routed to</th><th>Reason</th>
                                    <th>Status</th><th className="datt-right">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr><td colSpan={8} className="datt-empty">
                                        Nothing here. Requests from the employee app land in this list.
                                    </td></tr>
                                )}
                                {rows.map((r) => (
                                    <tr key={r.request_id}>
                                        <td className="datt-muted datt-nowrap">
                                            {String(r.raised_time).slice(0, 10)}
                                        </td>
                                        <td>
                                            <div className="datt-strong">{r.emp_name}</div>
                                            <div className="datt-mono datt-muted">{r.emp_id}</div>
                                        </td>
                                        <td>{MONTHS[r.month - 1]} {r.year}</td>
                                        <td>
                                            <span className={`datt-pill ${typeMeta(r.request_type).cls}`}
                                                  style={{ marginLeft: 0 }}>
                                                {typeMeta(r.request_type).label}
                                            </span>
                                            {r.request_type === "support" && r.work_date && (
                                                <div className="datt-muted datt-small" style={{ marginTop: 4 }}>
                                                    {fmtDay(r.work_date)} · {portionLabel(r.day_portion)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="datt-muted datt-small">
                                            {r.approver_name || "No approver resolved"}
                                        </td>
                                        <td className="datt-muted datt-reason">{r.reason}</td>
                                        <td>
                                            <span className={`datt-pill ${
                                                r.status === "pending" ? "is-amber"
                                                : r.status === "rejected" ? "is-red" : "is-green"}`}
                                                style={{ marginLeft: 0 }}>
                                                {r.status}
                                            </span>
                                        </td>
                                        <td className="datt-right datt-nowrap">
                                            {r.status === "pending" ? (
                                                <span className="datt-actions">
                                                    <button type="button" className="datt-btn is-ghost is-sm"
                                                        onClick={() => openSheet(r.request_id)}>View sheet</button>
                                                    <button type="button" className="datt-btn is-ok is-sm" disabled={busy}
                                                        onClick={() => decide(r.request_id,
                                                            r.request_type === "edit" ? "accept" : "approve")}>
                                                        {r.request_type === "edit" ? "Accept" : "Approve"}
                                                    </button>
                                                    <button type="button" className="datt-btn is-danger is-sm" disabled={busy}
                                                        onClick={() => { setRejectNote(""); setRejectReq(r); }}>Reject</button>
                                                </span>
                                            ) : (
                                                <span className="datt-muted datt-small">
                                                    {r.status} · {r.decided_time ? String(r.decided_time).slice(0, 10) : "-"}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {detail && (
                <SheetModal
                    detail={detail} busy={busy}
                    onClose={() => setDetail(null)}
                    onDecide={(decision, note) => decide(detail.request.request_id, decision, note)}
                    onExport={() => exportSheet(detail.employee.emp_id, detail.request.year, detail.request.month)}
                />
            )}

            {rejectReq && (
                <div className="datt-modal-backdrop" onClick={() => setRejectReq(null)}>
                    <div className="datt-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="datt-modal-head">
                            <span className="datt-modal-title">
                                Reject {typeMeta(rejectReq.request_type).label.toLowerCase()} - {rejectReq.emp_name}
                            </span>
                            <button type="button" className="datt-btn is-quiet is-sm"
                                onClick={() => setRejectReq(null)}>Close</button>
                        </div>
                        <div className="datt-modal-body">
                            <p className="datt-modal-note">
                                {MONTHS[rejectReq.month - 1]} {rejectReq.year}
                                {rejectReq.request_type === "support" && rejectReq.work_date
                                    ? ` · ${fmtDay(rejectReq.work_date)} (${portionLabel(rejectReq.day_portion)})` : ""}
                            </p>
                            <label className="datt-label" htmlFor="reject-reason">
                                Reason for rejection (required - the employee sees this)
                            </label>
                            <textarea id="reject-reason" rows={3} className="datt-note-input" autoFocus
                                value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} />
                        </div>
                        <div className="datt-modal-foot">
                            <button type="button" className="datt-btn is-quiet" disabled={busy}
                                onClick={() => setRejectReq(null)}>Cancel</button>
                            <button type="button" className="datt-btn is-danger" disabled={busy || !rejectNote.trim()}
                                onClick={async () => { await decide(rejectReq.request_id, "reject", rejectNote.trim()); setRejectReq(null); }}>
                                {busy ? "Rejecting…" : "Reject"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {toast && <div className="datt-toast">{toast}</div>}
            </div>
          </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
function SheetModal({ detail, busy, onClose, onDecide, onExport }) {
    const [note, setNote] = useState("");
    const { request, employee, days, marks, summary } = detail;
    const isEdit = request.request_type === "edit";
    const isSupport = request.request_type === "support";

    return (
        <div className="datt-modal-backdrop" onClick={onClose}>
            <div className="datt-modal is-wide" onClick={(e) => e.stopPropagation()}>
                <div className="datt-modal-head">
                    <span className="datt-modal-title">
                        {employee.emp_name} - {MONTHS[request.month - 1]} {request.year}
                    </span>
                    <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <button type="button" className="datt-btn is-export is-sm" onClick={onExport}
                            title="Export this sheet to Excel">
                            <FaFileExcel style={{ marginRight: 6 }} /> Export Excel
                        </button>
                        <button type="button" className="datt-btn is-quiet is-sm" onClick={onClose}>Close</button>
                    </span>
                </div>

                <div className="datt-modal-body">
                    {isSupport && (
                        <div className="datt-notice is-amber" style={{ marginBottom: 12 }}>
                            Weekend support claim for <strong>{fmtDay(request.work_date)}</strong> ({portionLabel(request.day_portion)}).
                            Approving marks that day <strong>P</strong> (it becomes a working day); the rest of the month is untouched.
                        </div>
                    )}
                    <p className="datt-modal-note">Reason given: {request.reason || "-"}</p>

                    <div className="datt-grid-wrap">
                        <table className="datt-grid">
                            <tbody>
                                <tr>
                                    <td className="datt-grid-sticky datt-grid-head">Day</td>
                                    {days.map((d) => (
                                        <td key={d.date}
                                            className={`datt-grid-cell datt-grid-head${d.dow === 0 || d.dow === 6 ? " is-weekend" : ""}`}>
                                            {d.dow_label}
                                        </td>
                                    ))}
                                </tr>
                                <tr>
                                    <td className="datt-grid-sticky datt-grid-head">Date</td>
                                    {days.map((d) => (
                                        <td key={d.date} className="datt-grid-cell datt-grid-date">{d.date.slice(8)}</td>
                                    ))}
                                </tr>
                                <tr>
                                    <td className="datt-grid-sticky datt-grid-name">
                                        {employee.emp_name}<span>{employee.emp_id}</span>
                                    </td>
                                    {days.map((d) => {
                                        const v = d.day_type === "WORK" ? (marks[d.date] || "") : "H";
                                        const cls = v === "P" ? "is-p" : v === "L" ? "is-l" : v === "H" ? "is-h" : "";
                                        const claimed = isSupport && d.date === request.work_date;
                                        return (
                                            <td key={d.date} className={`datt-grid-cell datt-grid-mark ${cls}${claimed ? " is-claim" : ""}`}
                                                title={claimed ? `${d.date} - support claim (${portionLabel(request.day_portion)})` : `${d.date} - ${d.label}`}>
                                                {v || "–"}
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className="datt-stats">
                        <Stat label="Present" value={summary.present_days} tone="green" />
                        <Stat label="Leave Taken" value={summary.leave_days}
                              tone={summary.leave_days > summary.allowed_leave ? "red" : ""} />
                        <Stat label="Allowed Leave" value={summary.allowed_leave} />
                        <Stat label="Total Days in Month" value={summary.days_in_month} />
                        <Stat label="Total Working Days" value={summary.working_days} />
                        <Stat label="Worked Days" value={summary.worked_days} />
                        <Stat label="Total Day Off" value={summary.days_off} />
                    </div>

                    {!summary.reconciles && (
                        <div className="datt-notice is-red" style={{ marginTop: 12 }}>
                            This sheet does not reconcile - the day buckets don't sum to the month.
                            Do not approve it; raise it with engineering.
                        </div>
                    )}

                    <label className="datt-label" htmlFor="note">
                        Note (required to reject - visible to the employee in the activity log)
                    </label>
                    <textarea id="note" rows={2} className="datt-note-input" value={note}
                        onChange={(e) => setNote(e.target.value)} />
                </div>

                <div className="datt-modal-foot">
                    <button type="button" className="datt-btn is-danger" disabled={busy || !note.trim()}
                        title={!note.trim() ? "Add a reason to reject" : undefined}
                        onClick={() => onDecide("reject", note.trim())}>Reject</button>
                    <button type="button" className="datt-btn is-ok" disabled={busy}
                        onClick={() => onDecide(isEdit ? "accept" : "approve", note)}>
                        {isEdit ? "Accept edit request" : isSupport ? "Approve support" : "Approve attendance"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, tone }) {
    return (
        <div className="datt-stat">
            <div className="datt-stat-label">{label}</div>
            <div className={`datt-stat-value ${tone ? `tone-${tone}` : ""}`}>{value}</div>
        </div>
    );
}
